import React, { useEffect, useRef } from "react";

// Minimal dependency-free signature pad. Draw with mouse or finger;
// parent reads the canvas via ref + hasDrawn flag. Kept intentionally
// small (visually and in pixels) so the stored PNG data URL stays a
// few KB. Shared by the /signup waiver step and the WaiverModal
// backfill for existing users.
const SignaturePad = ({ canvasRef, onDrawnChange }) => {
  const drawing = useRef(false);
  const hasDrawnRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1f2330";
    // White background so the exported PNG isn't transparent (renders
    // consistently wherever the admin views it later).
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, [canvasRef]);

  const pointFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handleDown = (e) => {
    e.preventDefault();
    if (e.target.setPointerCapture && e.pointerId != null) {
      try {
        e.target.setPointerCapture(e.pointerId);
      } catch {
        // Safari can throw on synthetic events; drawing still works.
      }
    }
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = pointFromEvent(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    // A dot for single taps so even the shortest stroke marks the pad.
    ctx.lineTo(p.x + 0.1, p.y + 0.1);
    ctx.stroke();
    if (!hasDrawnRef.current) {
      hasDrawnRef.current = true;
      onDrawnChange(true);
    }
  };

  const handleMove = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pointFromEvent(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };

  const handleUp = () => {
    drawing.current = false;
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    hasDrawnRef.current = false;
    onDrawnChange(false);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          height: 140,
          border: "2px dashed #b9c2d0",
          borderRadius: 8,
          background: "#fff",
          touchAction: "none",
          display: "block",
        }}
        onPointerDown={handleDown}
        onPointerMove={handleMove}
        onPointerUp={handleUp}
        onPointerLeave={handleUp}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 6,
        }}
      >
        <span style={{ fontSize: 12, color: "#5b6371" }}>
          Sign your name above with your finger or mouse
        </span>
        <button
          type="button"
          onClick={handleClear}
          style={{
            background: "none",
            border: "1px solid #d0d6e0",
            borderRadius: 6,
            padding: "4px 10px",
            fontSize: 12,
            cursor: "pointer",
            color: "#3a4150",
          }}
        >
          Clear
        </button>
      </div>
    </div>
  );
};

export default SignaturePad;
